// FILE: apps/admin/src/components/ConfirmDialog.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Privileged action confirmation modal enforcing mandatory reason and optional Telegram DM Step-Up 6-digit OTP verification.
//   SCOPE: ConfirmDialog component with integrated requestStepUp and verifyStepUp workflow.
//   DEPENDS: ../api.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   ConfirmDialog - Modal dialog collecting mandatory audit reason and optional Step-Up OTP grant token
//   ConfirmDialogProps - Props for ConfirmDialog component
// END_MODULE_MAP

import { type ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';

import { requestStepUp, verifyStepUp } from '../api.js';
import { AdminIcon } from './Icons.js';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  stepUpAction?: string;
  defaultReason?: string;
  danger?: boolean;
  children?: ReactNode;
  onCancel: () => void;
  onConfirm: (result: { reason: string; stepUpGrantToken?: string }) => Promise<void> | void;
}

// START_CONTRACT: ConfirmDialog
//   PURPOSE: Render confirmation dialog requiring a reason (>=3 chars) and optional 6-digit Telegram DM OTP Step-Up verification.
//   INPUTS: ConfirmDialogProps
//   OUTPUTS: React element or null
//   SIDE_EFFECTS: Requests and verifies Step-Up OTP when stepUpAction is specified
//   LINKS: M-ADMIN
// END_CONTRACT: ConfirmDialog
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Подтвердить действие',
  stepUpAction,
  defaultReason = '',
  danger = false,
  children,
  onCancel,
  onConfirm,
}: ConfirmDialogProps) {
  // START_BLOCK_CONFIRM_DIALOG
  const [reason, setReason] = useState(defaultReason);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState('');
  const [grantToken, setGrantToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!open) return null;

  const handleSendOtp = async () => {
    if (!stepUpAction) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await requestStepUp(stepUpAction);
      setChallengeId(res.challengeId);
    } catch (e) {
      setErr((e as Error).message || 'Не удалось отправить OTP в Telegram');
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!challengeId || !otpCode.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await verifyStepUp(challengeId, otpCode.trim());
      setGrantToken(res.grantToken);
    } catch (e) {
      setErr((e as Error).message || 'Неверный код Step-Up');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    const trimmedReason = reason.trim();
    if (trimmedReason.length < 3) {
      setErr('Укажите причину для журнала аудита (минимум 3 символа)');
      return;
    }
    if (stepUpAction && !grantToken) {
      setErr('Для этой операции требуется подтверждение Step-Up кодом из Telegram');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await onConfirm({
        reason: trimmedReason,
        stepUpGrantToken: grantToken ?? undefined,
      });
    } catch (e) {
      setErr((e as Error).message || 'Ошибка выполнения операции');
    } finally {
      setBusy(false);
    }
  };

  const dialogContent = (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="card confirm-modal" onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>{title}</h2>
        {description && (
          <p className="muted" style={{ marginBottom: 14 }}>
            {description}
          </p>
        )}

        {children && <div style={{ marginBottom: 14 }}>{children}</div>}

        <div style={{ marginBottom: 14 }}>
          <label className="field-label">Причина операции (обязательно для журнала аудита)</label>
          <input
            style={{ width: '100%' }}
            placeholder="Например: Компенсация задержки заказа #A1B2C3D4"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        {stepUpAction && (
          <div className="stepup-box">
            <div style={{ fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
              <AdminIcon name="lock" size={14} />
              <span>Требуется подтверждение Step-Up ({stepUpAction})</span>
            </div>
            {grantToken ? (
              <div className="ok" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <AdminIcon name="check" size={14} />
                <span>Одноразовый грант Step-Up получен</span>
              </div>
            ) : !challengeId ? (
              <button
                type="button"
                className="btn ghost sm"
                style={{ width: '100%', whiteSpace: 'normal', textAlign: 'center' }}
                disabled={busy}
                onClick={() => void handleSendOtp()}
              >
                Отправить 6-значный код в Telegram DM
              </button>
            ) : (
              <div className="row">
                <input
                  style={{ flex: '1 1 140px', minWidth: 0 }}
                  placeholder="Код из 6 цифр"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value)}
                />
                <button
                  type="button"
                  className="btn sm"
                  disabled={busy}
                  onClick={() => void handleVerifyOtp()}
                >
                  Проверить код
                </button>
              </div>
            )}
          </div>
        )}

        {err && (
          <div className="error" style={{ marginBottom: 12 }}>
            {err}
          </div>
        )}

        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="btn ghost" disabled={busy} onClick={onCancel}>
            Отмена
          </button>
          <button
            type="button"
            className={'btn' + (danger ? ' danger' : '')}
            disabled={busy}
            onClick={() => void handleSubmit()}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(dialogContent, document.body);
  }
  return dialogContent;
  // END_BLOCK_CONFIRM_DIALOG
}
